import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { Material } from "../../features/materials/models/Material";
import { fetchLibraryMaterials } from "../../features/messages/slice-extensions/attachments-slice-extension";
import { formatBytes } from "../../features/messages/utils/attachmentRules";
import ModalShell from "../modal/ModalShell";

interface LibraryPickerProps {
  onPick: (material: Material) => void;
  onClose: () => void;
}

/** Staff only: the organization's library; picking a file copies it into the thread. */
const LibraryPicker = ({ onPick, onClose }: LibraryPickerProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [materials, setMaterials] = useState<Material[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    dispatch(fetchLibraryMaterials())
      .unwrap()
      .then(setMaterials)
      .catch(() => setFailed(true));
  }, [dispatch]);

  return (
    <ModalShell onClose={onClose} labelledBy="library-picker-title">
      <div className="p-6">
        <h3 id="library-picker-title" className="mb-4 text-lg font-semibold text-grey-10 dark:text-grey-90">
          {t("messages.attach.material")}
        </h3>
        {failed && <p className="text-sm text-red-700 dark:text-red-400">{t("messages.attach.openFailed")}</p>}
        {materials?.length === 0 && (
          <p className="text-sm text-grey-40 dark:text-grey-80">{t("messages.attach.libraryEmpty")}</p>
        )}
        <ul className="max-h-80 divide-y divide-grey-90 overflow-y-auto dark:divide-primary-20">
          {materials?.map((material) => (
            <li key={material.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 py-2 text-start hover:bg-grey-95 dark:hover:bg-primary-20"
                onClick={() => onPick(material)}
              >
                <span className="truncate text-sm text-grey-20 dark:text-white">{material.fileName}</span>
                <span className="shrink-0 text-xs text-grey-40 dark:text-grey-80">
                  {formatBytes(material.sizeBytes, i18n.language)}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default LibraryPicker;
